import type { ExpenseLineItem, ExpenseResponseDto } from '@splitfiesta/shared';
import {
  allocateByWeights,
  fromMinorUnits,
  pickersOf,
  stakeOf,
  toMinorUnits,
  unitsLeft,
} from '@splitfiesta/shared';

import type { expenseItemsPreview } from './claimReconcile';

export type PickMember = { userId: string; isPlaceholder?: boolean };

export type PicksSummary = {
  asked: boolean;
  itemsPicked: number;
  itemsTotal: number;
  peopleKnow: number;
  peopleDone: number;
  peopleTotal: number;
  done: boolean;
  doneInMs: number | null;
  method: 'equal' | 'percentage' | 'shares';
};

export type PersonPickLine =
  | { state: 'picked'; source: 'app' | 'link'; pickedAt: string; items: number }
  | { state: 'notYet' }
  | { state: 'noApp' };

export type ClaimFooter = {
  items: number;
  itemsAmount: number;
  /** `rest` clamped at 0; kept for callers that never show a discount. */
  tipShare: number;
  total: number;
  /** Signed: `itemsAmount + rest === total` always; negative when the viewer's part of a discount outweighs the rest. */
  rest: number;
  /**
   * What `rest` holds. `tipTax`: the bill's tip and tax only. `both`: tip
   * and tax plus the viewer's share of units nobody picked. `unpicked`: no
   * tip or tax, so only that share (or nothing), net of any discount.
   * `discount`: `rest` is negative, the viewer's part of the discount net of
   * their unpicked share and any tip or tax.
   */
  restKind: 'tipTax' | 'unpicked' | 'both' | 'discount';
};

type PickExpense = Pick<
  ExpenseResponseDto,
  | 'pickedBy'
  | 'picksRequestedAt'
  | 'picksOpen'
  | 'lineItems'
  | 'lineItemParticipants'
  | 'unpickedSplit'
  | 'createdAt'
  | 'createdBy'
  | 'claimLinkOpen'
>;

const placeholderIds = (members: readonly PickMember[]) =>
  new Set(members.filter(m => m.isPlaceholder).map(m => m.userId));

/**
 * The server's rule: a placeholder can only pick through the link, so they
 * count while it is live, and stay counted once they have picked. A
 * participant missing from `members` (left the group) still has an account.
 */
const countedParticipants = (
  expense: PickExpense,
  members: readonly PickMember[]
): { people: string[]; accounts: string[] } => {
  const placeholders = placeholderIds(members);
  const picked = new Set((expense.pickedBy ?? []).map(p => p.userId));
  const listed = [...new Set(expense.lineItemParticipants ?? [])];
  return {
    people: listed.filter(
      id => !placeholders.has(id) || !!expense.claimLinkOpen || picked.has(id)
    ),
    accounts: listed.filter(id => !placeholders.has(id)),
  };
};

export function picksSummary(
  expense: PickExpense,
  members: readonly PickMember[]
): PicksSummary {
  const items = expense.lineItems ?? [];
  const { people, accounts } = countedParticipants(expense, members);
  const picks = (expense.pickedBy ?? []).filter(p => people.includes(p.userId));
  const doneIds = new Set(picks.map(p => p.userId));
  // Rows cached from an API that predates `picksOpen` work it out here.
  const done =
    expense.picksOpen !== undefined
      ? !expense.picksOpen
      : people.length > 0 && people.every(id => doneIds.has(id));
  const lastPick = Math.max(...picks.map(p => Date.parse(p.pickedAt)));
  return {
    asked: !!expense.picksRequestedAt,
    itemsPicked: items.filter(i => pickersOf(i).length > 0).length,
    itemsTotal: items.length,
    peopleKnow:
      accounts.length - (accounts.includes(expense.createdBy) ? 1 : 0),
    peopleDone: doneIds.size,
    peopleTotal: people.length,
    done,
    doneInMs:
      done && picks.length > 0
        ? lastPick - Date.parse(expense.createdAt)
        : null,
    method: expense.unpickedSplit?.method ?? 'equal',
  };
}

export function personPickLine(
  expense: Pick<ExpenseResponseDto, 'pickedBy' | 'lineItems'>,
  userId: string,
  members: readonly PickMember[]
): PersonPickLine {
  const pick = expense.pickedBy?.find(p => p.userId === userId);
  if (!pick)
    return placeholderIds(members).has(userId)
      ? { state: 'noApp' }
      : { state: 'notYet' };
  return {
    state: 'picked',
    source: pick.source,
    pickedAt: pick.pickedAt,
    items: (expense.lineItems ?? []).filter(i => stakeOf(i, userId) > 0).length,
  };
}

/** Whether `userId`'s amount is still only what they would pay if nobody else picked. */
export function isAmountForNow(
  expense: Pick<ExpenseResponseDto, 'pickedBy' | 'lineItems'>,
  userId: string,
  _members: readonly PickMember[],
  done: boolean
): boolean {
  return !done && !expense.pickedBy?.some(p => p.userId === userId);
}

/** Once the table was asked, saving with nothing ticked still tells the payer the viewer is done. */
export function canSaveClaim({
  dirty,
  asked,
  viewerPicked,
}: {
  dirty: boolean;
  asked: boolean;
  viewerPicked: boolean;
}): boolean {
  return dirty || (asked && !viewerPicked);
}

/** The viewer's picked units at the splitter's unit prices (pickers in userId order take consecutive units), in minor units; their share of unpicked units is left out. */
function pickedMinor(
  item: ExpenseLineItem,
  viewerId: string,
  participants: ReadonlySet<string>,
  currency: string
): number {
  const picks = (item.picks ?? [])
    .filter(p => participants.has(p.userId))
    .sort((a, b) => (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0));
  const prices = allocateByWeights(
    toMinorUnits(item.amount, currency),
    Array.from({ length: item.quantity }, () => 1),
    Array.from({ length: item.quantity }, (_, i) => i)
  );
  let next = 0;
  for (const p of picks) {
    if (p.userId === viewerId)
      return prices.slice(next, next + p.units).reduce((a, b) => a + b, 0);
    next += p.units;
  }
  return 0;
}

/**
 * Amounts in the receipt's currency; null while there is no preview.
 * `savedTotal` replaces the preview's total when the server's share is the
 * one to show. A discount larger than the extras makes `rest` negative and
 * clamps `tipShare` at 0 so it never reads as a negative tip.
 */
export function claimFooter(
  result: ReturnType<typeof expenseItemsPreview>,
  rows: readonly ExpenseLineItem[],
  viewerId: string,
  savedTotal?: number
): ClaimFooter | null {
  const { preview, entryCurrency, foreign } = result;
  if (!preview) return null;
  const participants = new Set(preview.entryShares.map(s => s.userId));
  const held = participants.has(viewerId)
    ? rows.filter(i => stakeOf(i, viewerId) > 0)
    : [];
  const itemsMinor = held.reduce(
    (sum, i) =>
      sum +
      (i.picks
        ? pickedMinor(i, viewerId, participants, entryCurrency)
        : toMinorUnits(
            preview.itemShares[i.id]?.find(s => s.userId === viewerId)
              ?.amount ?? 0,
            entryCurrency
          )),
    0
  );
  const unpickedMinor = participants.has(viewerId)
    ? rows.reduce((sum, i) => {
        // The splitter gives a legacy row wholly to its sharers; one with none is everyone's to share.
        const left = i.picks
          ? unitsLeft(i)
          : i.sharedBy.length
            ? 0
            : i.quantity;
        if (left <= 0) return sum;
        const share = toMinorUnits(
          preview.itemShares[i.id]?.find(s => s.userId === viewerId)?.amount ??
            0,
          entryCurrency
        );
        const picked = i.picks
          ? pickedMinor(i, viewerId, participants, entryCurrency)
          : 0;
        return sum + Math.max(0, share - picked);
      }, 0)
    : 0;
  const hasExtras = preview.restAmount > 0;
  const shares = foreign ? preview.entryShares : preview.splits;
  const totalMinor = toMinorUnits(
    savedTotal ?? shares.find(s => s.userId === viewerId)?.amount ?? 0,
    entryCurrency
  );
  const restMinor = totalMinor - itemsMinor;
  return {
    items: held.length,
    itemsAmount: fromMinorUnits(itemsMinor, entryCurrency),
    tipShare: fromMinorUnits(Math.max(0, restMinor), entryCurrency),
    total: fromMinorUnits(totalMinor, entryCurrency),
    rest: fromMinorUnits(restMinor, entryCurrency),
    restKind:
      restMinor < 0
        ? 'discount'
        : !hasExtras
          ? 'unpicked'
          : unpickedMinor > 0
            ? 'both'
            : 'tipTax',
  };
}
